import React, { useEffect, useState } from 'react';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  MapPin, PlusCircle, FileText, Receipt, TrendingUp,
  AlertCircle, CheckCircle2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

// Human-readable date; safely renders "—" if we get null / empty.
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const statusPillClass = (status) => {
  switch (status) {
    case 'active':
    case 'trialing':
    case 'paid':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'past_due':
    case 'open':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'canceled':
    case 'uncollectible':
    case 'void':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

// Small stat block used inside the overview card.
function Stat({ label, value, sublabel, testid }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <p className="text-[11px] uppercase tracking-wider text-[#64748B] font-medium">{label}</p>
      <p className="text-lg font-semibold text-[#0F172A] font-['Montserrat'] mt-0.5 truncate">
        {value ?? '—'}
      </p>
      {sublabel && <p className="text-xs text-[#94A3B8] mt-0.5">{sublabel}</p>}
    </div>
  );
}

export default function SubscriptionPage() {
  const [overview, setOverview] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [addLocCount, setAddLocCount] = useState(1);
  const [addLocBusy, setAddLocBusy] = useState(false);
  const [addSurveyOpen, setAddSurveyOpen] = useState(false);
  const [addSurveyCount, setAddSurveyCount] = useState(1);
  const [addSurveyBusy, setAddSurveyBusy] = useState(false);

  useEffect(() => {
    Promise.all([fetchOverview(), fetchBilling()]).finally(() => setLoading(false));
  }, []);

  const fetchOverview = async () => {
    try {
      const r = await fetch(`${API}/subscriptions/overview`, { credentials: 'include' });
      if (r.ok) setOverview(await r.json());
    } catch (e) { console.error('overview:', e); }
  };

  const fetchBilling = async () => {
    try {
      const r = await fetch(`${API}/subscriptions/billing-history`, { credentials: 'include' });
      if (r.ok) setBilling(await r.json());
    } catch (e) { console.error('billing:', e); }
  };

  const handleAddLocations = async () => {
    if (!addLocCount || addLocCount < 1) return;
    setAddLocBusy(true);
    try {
      const r = await fetch(`${API}/subscriptions/add-locations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additional: Number(addLocCount) }),
      });
      if (r.ok) {
        toast.success(`${addLocCount} location${addLocCount > 1 ? 's' : ''} added.`);
        setAddLocOpen(false);
        setAddLocCount(1);
        await fetchOverview();
        await fetchBilling();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail?.message || err.detail || 'Failed to add locations');
      }
    } finally { setAddLocBusy(false); }
  };

  const handleAddSurveys = async () => {
    if (!addSurveyCount || addSurveyCount < 1) return;
    setAddSurveyBusy(true);
    try {
      const r = await fetch(`${API}/subscriptions/add-active-surveys`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ additional: Number(addSurveyCount) }),
      });
      if (r.ok) {
        toast.success(`${addSurveyCount} additional active survey${addSurveyCount > 1 ? 's' : ''} added.`);
        setAddSurveyOpen(false);
        setAddSurveyCount(1);
        await fetchOverview();
        await fetchBilling();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail?.message || err.detail || 'Failed to add active surveys');
      }
    } finally { setAddSurveyBusy(false); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24" data-testid="subscription-loading">
          <div className="text-[#64748B]">Loading subscription…</div>
        </div>
      </DashboardLayout>
    );
  }

  const noSub = !overview?.product;
  const addOn = overview?.add_on_active_survey;
  const locations = overview?.locations || { purchased: 0, used: 0, available: 0 };
  const surveys = overview?.active_surveys || {};
  const pendingReduction = locations.pending_reduction;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl" data-testid="subscription-page">
        {/* Page header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']" data-testid="subscription-title">
            Subscription & Billing
          </h1>
          <p className="text-[#64748B] mt-1">
            {overview?.is_manual
              ? 'Your subscription is managed by satisFIND. Your current plan and billing arrangement are shown below.'
              : 'Manage your VOX plan, locations, add-on surveys and invoices.'}
          </p>
        </div>

        {/* Jun 2026 — offline / manually-managed accounts (e.g. Mystery
            Shopping paid by cheque). Show the actual status + offline billing
            arrangement instead of VOX checkout messaging. No Stripe. */}
        {overview?.is_manual && (
          <Card data-testid="offline-billing-card">
            <CardContent className="py-5 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-[#1A3E6F]" />
              <div className="text-sm text-[#334155] space-y-1">
                <p>
                  <strong>{overview.plan_label || 'Your plan'}</strong> — status{' '}
                  <span className="font-semibold capitalize">{overview.status || 'active'}</span>.
                </p>
                <p className="text-[#64748B]">
                  Billing arrangement: <strong>Offline</strong> — managed directly by satisFIND.
                  No online payment or card is required for this account.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {noSub && (
          <Card data-testid="no-subscription-card">
            <CardContent className="py-8 text-center space-y-3">
              <AlertCircle className="w-8 h-8 text-amber-500 mx-auto" />
              <p className="font-medium text-[#0F172A]">No active subscription</p>
              <p className="text-sm text-[#64748B]">
                You don't have a VOX subscription yet. Start one from the pricing page.
              </p>
              <a
                href="https://vox.satisfind.com/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-sm text-[#1A3E6F] font-semibold underline"
                data-testid="subscription-view-plans"
              >
                View plans →
              </a>
            </CardContent>
          </Card>
        )}

        {/* Phase 4 — lifecycle banner surfaces the customer-facing state
            (past_due grace / restricted / read-only / deletion). Only
            renders when the tenant has a subscription in a non-active
            state. */}
        {!noSub && overview.lifecycle_state && (
          <div
            data-testid={`lifecycle-banner-${overview.lifecycle_state}`}
            className={
              `flex items-start gap-3 rounded-md border p-4 ` +
              (overview.lifecycle_state === 'restricted' || overview.lifecycle_state === 'deletion_scheduled'
                ? 'bg-rose-50 border-rose-200 text-rose-900'
                : 'bg-amber-50 border-amber-200 text-amber-900')
            }
          >
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
            <div className="text-sm">
              {overview.lifecycle_state === 'restricted' && (
                <>Your account is <strong>restricted</strong> due to unpaid invoices. You can still log in, view dashboards, reports and historical responses, but can't collect new responses or edit surveys. Resolve billing below to restore full access.</>
              )}
              {overview.lifecycle_state === 'read_only' && (
                <>Your subscription has ended. You have a <strong>read-only window until {fmtDate(overview.read_only_until)}</strong> to log in, view historical data and download exports. Renew below to restore full access.</>
              )}
              {overview.lifecycle_state === 'deletion_scheduled' && (
                <>Your read-only window has ended. Contact support if you need to recover data.</>
              )}
            </div>
          </div>
        )}
        {!noSub && !overview.lifecycle_state && overview.status === 'past_due' && overview.grace_until && (
          <div
            data-testid="lifecycle-banner-past_due_grace"
            className="flex items-start gap-3 rounded-md border p-4 bg-amber-50 border-amber-200 text-amber-900"
          >
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
            <div className="text-sm">
              <strong>Payment issue detected.</strong> You have full access until{' '}
              <strong>{fmtDate(overview.grace_until)}</strong>. Resolve billing before then to avoid restricted access.
            </div>
          </div>
        )}

        {!noSub && (
          <>
            {/* ─── 1. Subscription Overview ─── */}
            <Card data-testid="subscription-overview-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-['Montserrat'] text-lg">Subscription overview</CardTitle>
                <span
                  className={`text-xs px-3 py-1 rounded-full font-medium border ${statusPillClass(overview.status)}`}
                  data-testid="subscription-status-pill"
                >
                  {overview.status || 'inactive'}
                </span>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
                  <Stat testid="overview-product" label="Product" value={overview.plan_label} sublabel={overview.product?.toUpperCase()} />
                  <Stat
                    testid="overview-billing-cycle"
                    label="Billing"
                    value={overview.billing_cycle === 'annual' ? 'Annual' : (overview.billing_cycle === 'monthly' ? 'Monthly' : '—')}
                  />
                  <Stat
                    testid="overview-renewal"
                    label="Next renewal"
                    value={fmtDate(overview.renewal_date)}
                    sublabel={overview.cancel_at_period_end ? 'Cancels at period end' : null}
                  />
                  <Stat
                    testid="overview-locations"
                    label="Locations"
                    value={`${locations.used} / ${locations.purchased}`}
                    sublabel={`${locations.available} available`}
                  />
                  <Stat
                    testid="overview-active-surveys"
                    label="Active surveys"
                    value={`${surveys.used ?? 0} / ${surveys.limit ?? '∞'}`}
                    sublabel={`Included ${surveys.included ?? 0} + Add-on ${surveys.additional ?? 0}`}
                  />
                  <Stat
                    testid="overview-quantity"
                    label="Paid Locations"
                    value={locations.purchased}
                  />
                </div>

                {pendingReduction && (
                  <div
                    data-testid="pending-reduction-banner"
                    className="mt-6 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800"
                  >
                    <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      Location reduction to <strong>{pendingReduction.new_quantity}</strong> scheduled for{' '}
                      <strong>{fmtDate(pendingReduction.effective_at)}</strong>. No change will occur until then.
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ─── 2. Manage Locations ─── (Stripe-store only; hidden for
                offline/manually-managed accounts) */}
            {!overview.is_manual && (
            <Card data-testid="manage-locations-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#1A3E6F]" />
                  <CardTitle className="font-['Montserrat'] text-lg">Manage locations</CardTitle>
                </div>
                <Dialog open={addLocOpen} onOpenChange={setAddLocOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-[#1A3E6F] text-[#1A3E6F]"
                      data-testid="add-locations-btn"
                    >
                      <PlusCircle className="w-4 h-4 mr-1.5" /> Add locations
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[440px]">
                    <DialogHeader>
                      <DialogTitle className="font-['Montserrat']">Add locations</DialogTitle>
                      <DialogDescription>
                        Increases your Stripe subscription quantity now, prorated through your existing renewal date
                        {overview.renewal_date ? ` (${fmtDate(overview.renewal_date)})` : ''}.
                        Your subscription term does not change; the extra locations join your existing subscription.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                      <Label htmlFor="loc-count">How many locations to add?</Label>
                      <Input
                        id="loc-count"
                        type="number"
                        min={1}
                        value={addLocCount}
                        onChange={(e) => setAddLocCount(Number(e.target.value) || 1)}
                        data-testid="add-locations-count"
                      />
                    </div>
                    <DialogFooter className="mt-4">
                      <Button variant="ghost" onClick={() => setAddLocOpen(false)} disabled={addLocBusy}>Cancel</Button>
                      <Button
                        onClick={handleAddLocations}
                        disabled={addLocBusy}
                        className="bg-[#1A3E6F] hover:bg-[#122c50] text-white"
                        data-testid="add-locations-confirm"
                      >
                        {addLocBusy ? 'Adding…' : `Add ${addLocCount} location${addLocCount > 1 ? 's' : ''}`}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#334155]">
                  You are using <strong data-testid="locations-used">{locations.used}</strong> of{' '}
                  <strong data-testid="locations-purchased">{locations.purchased}</strong> purchased locations
                  {locations.available > 0
                    ? <> — <strong data-testid="locations-available">{locations.available}</strong> available.</>
                    : <> — <span className="text-rose-700 font-medium">at capacity</span>.</>}
                </p>
                <p className="text-xs text-[#64748B] mt-2">
                  Closed locations retain their history and remain counted for the current paid term.
                </p>
              </CardContent>
            </Card>
            )}

            {/* ─── 3. Add-on Active Survey ─── (Stripe-store only) */}
            {!overview.is_manual && (
            <Card data-testid="addon-active-survey-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#1A3E6F]" />
                  <CardTitle className="font-['Montserrat'] text-lg">Additional active surveys</CardTitle>
                </div>
                {addOn?.available ? (
                  <Dialog open={addSurveyOpen} onOpenChange={setAddSurveyOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="border-[#1A3E6F] text-[#1A3E6F]"
                        data-testid="add-active-survey-btn"
                      >
                        <PlusCircle className="w-4 h-4 mr-1.5" /> Buy add-on
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[440px]">
                      <DialogHeader>
                        <DialogTitle className="font-['Montserrat']">Add active-survey capacity</DialogTitle>
                        <DialogDescription>
                          US${addOn.price_per_year_usd} per year per additional active survey. First charge is prorated to your
                          existing renewal date{overview.renewal_date ? ` (${fmtDate(overview.renewal_date)})` : ''};
                          renews at ${addOn.price_per_year_usd}/year × quantity on the same renewal date as your main subscription.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3 mt-2">
                        <Label htmlFor="survey-count">How many additional active surveys?</Label>
                        <Input
                          id="survey-count"
                          type="number"
                          min={1}
                          value={addSurveyCount}
                          onChange={(e) => setAddSurveyCount(Number(e.target.value) || 1)}
                          data-testid="add-active-survey-count"
                        />
                      </div>
                      <DialogFooter className="mt-4">
                        <Button variant="ghost" onClick={() => setAddSurveyOpen(false)} disabled={addSurveyBusy}>Cancel</Button>
                        <Button
                          onClick={handleAddSurveys}
                          disabled={addSurveyBusy}
                          className="bg-[#1A3E6F] hover:bg-[#122c50] text-white"
                          data-testid="add-active-survey-confirm"
                        >
                          {addSurveyBusy ? 'Adding…' : `Add ${addSurveyCount} for US$${(addSurveyCount * addOn.price_per_year_usd)}/yr`}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <span className="text-xs text-[#94A3B8]" data-testid="addon-disabled-reason">
                    {addOn?.reason_disabled || 'Not available'}
                  </span>
                )}
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#334155]">
                  You can activate up to{' '}
                  <strong data-testid="active-survey-limit">{surveys.limit ?? '∞'}</strong> surveys at the same time
                  {' '}(<strong data-testid="active-survey-used">{surveys.used ?? 0}</strong> currently active,
                  {' '}<span data-testid="active-survey-included">{surveys.included ?? 0}</span> included +{' '}
                  <span data-testid="active-survey-additional">{surveys.additional ?? 0}</span> add-on).
                </p>
                {!addOn?.available && overview.billing_cycle === 'monthly' && (
                  <p className="text-xs text-[#94A3B8] mt-2">
                    Additional active surveys are available with annual plans. Switch to annual billing at your next renewal
                    to unlock this add-on.
                  </p>
                )}
              </CardContent>
            </Card>
            )}

            {/* ─── 4. Billing History ─── (Stripe invoices; hidden for
                offline/manually-managed accounts) */}
            {!overview.is_manual && (
            <Card data-testid="billing-history-card">
              <CardHeader>
                <CardTitle className="font-['Montserrat'] text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#1A3E6F]" />
                  Billing history
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!billing?.invoices?.length ? (
                  <p className="text-sm text-[#64748B] py-4 text-center" data-testid="billing-empty">
                    No invoices yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Invoice / Receipt</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {billing.invoices.map((inv) => (
                          <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                            <TableCell className="whitespace-nowrap">{fmtDate(inv.date)}</TableCell>
                            <TableCell className="max-w-xs truncate">{inv.description}</TableCell>
                            <TableCell className="whitespace-nowrap font-medium">
                              {inv.amount_display}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full border ${statusPillClass(inv.status)}`}
                                data-testid={`invoice-status-${inv.invoice_id}`}
                              >
                                {inv.status}
                                {inv.paid && <CheckCircle2 className="inline-block w-3 h-3 ml-1" />}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                {inv.hosted_invoice_url && (
                                  <a
                                    href={inv.hosted_invoice_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-[#1A3E6F] font-semibold hover:underline inline-flex items-center gap-1"
                                    data-testid={`invoice-view-${inv.invoice_id}`}
                                  >
                                    <FileText className="w-3.5 h-3.5" /> Invoice
                                  </a>
                                )}
                                {inv.receipt_url && (
                                  <a
                                    href={inv.receipt_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-[#1A3E6F] font-semibold hover:underline inline-flex items-center gap-1"
                                    data-testid={`receipt-view-${inv.invoice_id}`}
                                  >
                                    <Receipt className="w-3.5 h-3.5" /> Receipt
                                  </a>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <p className="text-xs text-[#94A3B8] mt-4">
                  Invoices and payment receipts are also emailed to your billing address by Stripe on every successful payment.
                </p>
              </CardContent>
            </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
